import React, { useState, useEffect, useCallback } from 'react';
import { getCompanies, updateCompany, createCompany } from '../services/api';
import './CompanyManagement.css';

const NEW_COMPANY = { isNew: true };

function CompanyManagement({ embedded = false }) {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', mandantennummer: '', anschrift: '', duns: '', steuernummer: '' });
  const [saving, setSaving] = useState(false);

  const isNew = editModal === NEW_COMPANY;

  const loadCompanies = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getCompanies();
      setCompanies(data.companies || []);
    } catch (err) {
      setError(err.message || 'Fehler beim Laden der Firmen');
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  const openNew = () => {
    setEditModal(NEW_COMPANY);
    setEditForm({ name: '', mandantennummer: '', anschrift: '', duns: '', steuernummer: '' });
  };

  const openEdit = (company) => {
    setEditModal(company);
    setEditForm({
      name: company.name || '',
      mandantennummer: company.mandantennummer || '',
      anschrift: company.anschrift || '',
      duns: company.duns || '',
      steuernummer: company.steuernummer || '',
    });
  };

  const closeEdit = () => {
    setEditModal(null);
  };

  const handleSave = async () => {
    if (isNew) {
      const mandantennummer = (editForm.mandantennummer || '').trim();
      const name = (editForm.name || '').trim();
      if (!name) {
        setError('Name ist erforderlich');
        return;
      }
      if (!mandantennummer) {
        setError('Mandantennummer ist erforderlich');
        return;
      }
      try {
        setSaving(true);
        setError('');
        await createCompany({
          name,
          mandantennummer,
          anschrift: editForm.anschrift || undefined,
          duns: editForm.duns || undefined,
          steuernummer: editForm.steuernummer || undefined,
        });
        await loadCompanies();
        closeEdit();
      } catch (err) {
        setError(err.message || 'Fehler beim Anlegen');
      } finally {
        setSaving(false);
      }
      return;
    }
    if (!editModal?.id) return;
    try {
      setSaving(true);
      setError('');
      await updateCompany(editModal.id, {
        name: editForm.name || undefined,
        anschrift: editForm.anschrift || undefined,
        duns: editForm.duns || undefined,
        steuernummer: editForm.steuernummer || undefined,
      });
      await loadCompanies();
      closeEdit();
    } catch (err) {
      setError(err.message || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="company-management">
      {!embedded && <h2>Firmenverwaltung</h2>}
      {error && <div className="error-message">{error}</div>}
      {loading ? (
        <div className="loading">Lade Firmen...</div>
      ) : (
        <>
        <div className="company-toolbar">
          <button type="button" className="btn btn-primary" onClick={openNew}>
            Neue Firma
          </button>
        </div>
        <table className="admin-table">
          <thead>
            <tr>
              <th className="col-id">ID</th>
              <th>Name</th>
              <th>Mandantennummer</th>
              <th>Anschrift</th>
              <th>DUNS</th>
              <th>Steuernummer</th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {companies.length === 0 ? (
              <tr>
                <td colSpan="7" style={{ textAlign: 'center' }}>
                  Keine Firmen gefunden
                </td>
              </tr>
            ) : (
              companies.map((c) => (
                <tr key={c.id}>
                  <td className="col-id">{c.id}</td>
                  <td>{c.name}</td>
                  <td>{c.mandantennummer}</td>
                  <td className="col-anschrift">{c.anschrift || '–'}</td>
                  <td>{c.duns || '–'}</td>
                  <td>{c.steuernummer || '–'}</td>
                  <td>
                    <button type="button" className="btn btn-secondary" onClick={() => openEdit(c)}>
                      Bearbeiten
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </>
      )}

      {editModal && (
        <div className="modal-overlay" onClick={closeEdit}>
          <div className="modal-content company-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{isNew ? 'Neue Firma' : 'Firma bearbeiten'}</h3>
            <div className="modal-form">
              <div className="form-row">
                <label>Mandantennummer</label>
                {isNew ? (
                  <input
                    type="text"
                    value={editForm.mandantennummer}
                    onChange={(e) => setEditForm((f) => ({ ...f, mandantennummer: e.target.value }))}
                  />
                ) : (
                  <span className="readonly-value">{editModal.mandantennummer}</span>
                )}
              </div>
              <div className="form-row">
                <label>Name</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                />
              </div>
              <div className="form-row">
                <label>Anschrift</label>
                <textarea
                  value={editForm.anschrift}
                  onChange={(e) => setEditForm((f) => ({ ...f, anschrift: e.target.value }))}
                  rows={3}
                />
              </div>
              <div className="form-row">
                <label>DUNS</label>
                <input
                  type="text"
                  value={editForm.duns}
                  onChange={(e) => setEditForm((f) => ({ ...f, duns: e.target.value }))}
                />
              </div>
              <div className="form-row">
                <label>Steuernummer</label>
                <input
                  type="text"
                  value={editForm.steuernummer}
                  onChange={(e) => setEditForm((f) => ({ ...f, steuernummer: e.target.value }))}
                />
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Speichern…' : 'Speichern'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={closeEdit}>
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CompanyManagement;
